/**
 * Seats that leave the layout and come back with a fade (a war: everyone not fighting). `out`:
 * who should be gone. Someone newly out is `fading` (still laid out, fading to nothing) for `ms`,
 * then `gone` (out of the layout, so the rest can take the room). Someone back in is laid out
 * again at once and is `back` (fading in) for `ms`. Whoever is out when this mounts is gone at once.
 */
import { useEffect, useState } from 'react';
import type { PlayerId } from '@kadice/shared';

interface FadeState {
  key: string;
  out: readonly PlayerId[];
  gone: readonly PlayerId[];
  back: readonly PlayerId[];
}

const NONE: readonly PlayerId[] = [];

export function useFadeOut(out: readonly PlayerId[], ms: number) {
  const key = [...out].sort((a, b) => a - b).join(',');
  const [state, setState] = useState<FadeState>({ key, out, gone: out, back: NONE });
  let current = state;
  if (state.key !== key) {
    const returning = state.gone.filter((id) => !out.includes(id));
    current = {
      key,
      out,
      gone: state.gone.filter((id) => out.includes(id)),
      back: returning.length > 0 ? [...state.back.filter((id) => !out.includes(id)), ...returning] : state.back,
    };
    setState(current);
  }
  const fading = current.out.filter((id) => !current.gone.includes(id));
  const anyFading = fading.length > 0;
  useEffect(() => {
    if (!anyFading) return;
    const t = setTimeout(() => setState((s) => (s.key === key ? { ...s, gone: s.out } : s)), ms);
    return () => clearTimeout(t);
  }, [key, anyFading, ms]);
  const back = current.back;
  useEffect(() => {
    if (back.length === 0) return;
    const t = setTimeout(() => setState((s) => (s.back === back ? { ...s, back: NONE } : s)), ms);
    return () => clearTimeout(t);
  }, [back, ms]);
  return { fading, gone: current.gone, back };
}
